import React, { useEffect, useState } from "react";
import { BellRing, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { ukDate } from "../lib/dates";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const NudgeDialog = ({ courseId, target, onClose }) => {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (target) setMsg(`Hi ${(target.name || "").split(" ")[0]}, I noticed your "${target.lesson_title}" assignment is still outstanding. Is everything okay? Let me know if I can help — I'd love to see your work.`);
  }, [target]);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/courses/${courseId}/reminder-alerts/message`, { member_id: target.member_id, lesson_id: target.lesson_id, message: msg });
      toast.success(data.sms === "sent" ? "Message sent to their portal and by text" : "Message sent to their portal");
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="nudge-message-dialog">
        <DialogHeader>
          <DialogTitle>Message {target?.name}</DialogTitle>
          <DialogDescription>Goes to their portal inbox, and by text if they have a mobile. Their number stays private.</DialogDescription>
        </DialogHeader>
        <textarea data-testid="nudge-message-input" rows={5} maxLength={500} value={msg} onChange={(e) => setMsg(e.target.value)}
          className="w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl outline-none px-3 py-2.5 text-sm" />
        <div className="text-[11px] text-right" style={{ color: "var(--sj-text-3)" }}>{msg.length}/500</div>
        <DialogFooter>
          <button data-testid="nudge-message-cancel" onClick={onClose} className="rounded-full px-5 py-2 border text-sm" style={{ borderColor: "var(--sj-border)" }}>Cancel</button>
          <button data-testid="nudge-message-send" disabled={busy || msg.trim().length < 2} onClick={send} className="rounded-full px-5 py-2 text-white text-sm font-semibold disabled:opacity-50" style={{ background: "var(--sj-primary)" }}>{busy ? "Sending…" : "Send message"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ReminderAlerts = ({ courseId, cohort, refreshKey }) => {
  const [rows, setRows] = useState(null);
  const [target, setTarget] = useState(null);
  useEffect(() => { (async () => {
    try { const { data } = await api.get(`/courses/${courseId}/reminder-alerts`); setRows(data || []); }
    catch (e) { setRows([]); }
  })(); }, [courseId, refreshKey]);
  const list = (rows || []).filter((r) => !cohort || r.cohort_id === cohort);
  if (!list.length) return null;
  return (
    <div className="rounded-xl border p-4 mb-5" style={{ background: "#FFF8EE", borderColor: "#F3D9B1" }} data-testid="reminder-alerts-card">
      <div className="flex items-center gap-2 mb-1">
        <BellRing className="h-4 w-4" style={{ color: "#B45309" }} />
        <h3 className="font-semibold text-sm" style={{ color: "#8a5a1a" }}>Reminded twice, still not handed in ({list.length})</h3>
      </div>
      <p className="text-xs mb-3" style={{ color: "#8a5a1a" }}>These learners have had two or more reminders (from class reps or the automatic due-date reminder). A personal word from you may help.</p>
      <ul className="divide-y" style={{ borderColor: "#F3D9B1" }}>
        {list.map((r) => (
          <li key={`${r.lesson_id}:${r.member_id}`} className="py-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm" data-testid={`reminder-alert-${r.member_id}-${r.lesson_id}`}>
            <span className="font-medium" style={{ color: "var(--sj-text)" }}>{r.name}<span className="font-normal" style={{ color: "var(--sj-text-2)" }}> · {r.lesson_title}{r.cohort_name ? ` · ${r.cohort_name}` : ""}</span></span>
            <span className="flex flex-wrap items-center gap-2 text-xs" style={{ color: "var(--sj-text-3)" }}>
              Reminded {r.count}× · last {ukDate(r.last_at)}{r.due_date ? ` · due ${ukDate(r.due_date)}` : ""}
              <button data-testid={`reminder-alert-message-${r.member_id}-${r.lesson_id}`} onClick={() => setTarget(r)} className="inline-flex items-center gap-1 rounded-full px-3 py-1 font-semibold border bg-white hover:bg-[#FFF3E0]" style={{ borderColor: "#B45309", color: "#B45309" }}>
                <MessageSquare className="h-3 w-3" /> Message
              </button>
            </span>
          </li>
        ))}
      </ul>
      <NudgeDialog courseId={courseId} target={target} onClose={() => setTarget(null)} />
    </div>
  );
};
